import React, { useState, useEffect, useCallback } from 'react';
import { FileText } from 'lucide-react';
import { useProject } from '../contexts/ProjectContext';
import api from '../api';
import toast from 'react-hot-toast';
import ProjectContextDisplay from '../components/ProjectContextDisplay';
import { useComplianceScore } from '../hooks/useComplianceScore';
import SOPGenerationTab from '../components/Analysis/SOPGenerationTab';
import Tooltip from '../components/Analysis/Tooltip';
import LoadingModal from '../components/LoadingModal';

interface AnalysisProgress {
  progress: number;
  completed: number;
  failed: number;
  pending: number;
  analyzing: number;
  total: number;
}

const SopGeneration: React.FC = () => {
  const { currentProject } = useProject();
  const [analysisProgress, setAnalysisProgress] = useState<AnalysisProgress | null>(null);
  const [analysisResults, setAnalysisResults] = useState<any>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Global compliance score management
  const { complianceScore, calculateComplianceScore } = useComplianceScore();

  const refreshAnalysisData = async () => {
    try {
      await loadAnalysisResults();
      console.log('Analysis data refreshed successfully');
    } catch (error) {
      console.error('Failed to refresh analysis data:', error);
    }
  };

  const refreshAnalysisDataWithFeedback = async () => {
    setIsRefreshing(true);
    try {
      await refreshAnalysisData();
      setTimeout(() => setIsRefreshing(false), 500);
    } catch (error) {
      setIsRefreshing(false);
      toast.error('Failed to refresh data');
    }
  };

  const loadAnalysisProgress = useCallback(async () => {
    if (!currentProject) return;
    try {
      const response = await api.get(`/api/analysis/progress/${currentProject.id}`);
      setAnalysisProgress(response.data);
    } catch (error) {
      console.error('Failed to load analysis progress');
    }
  }, [currentProject]);

  const loadAnalysisResults = useCallback(async () => {
    if (!currentProject) return;
    try {
      const timestamp = Date.now();
      const response = await api.get(`/api/analysis/results/${currentProject.id}?_t=${timestamp}`);

      console.log('Loaded fresh analysis results:', response.data);
      setAnalysisResults(response.data);

      // Calculate and update global compliance score
      const synthesisData = response.data.process_synthesis;
      calculateComplianceScore(synthesisData, response.data);
    } catch (error) {
      console.error('Failed to load analysis results:', error);
    }
  }, [currentProject, calculateComplianceScore]);

  useEffect(() => {
    if (currentProject) {
      loadAnalysisProgress();
      loadAnalysisResults();
    }
  }, [currentProject]);

  // Poll for analysis results while analyzing
  useEffect(() => {
    let resultsInterval: NodeJS.Timeout | null = null;
    if (isAnalyzing) {
      resultsInterval = setInterval(() => {
        loadAnalysisResults();
      }, 3000);
    }
    return () => {
      if (resultsInterval) clearInterval(resultsInterval);
    };
  }, [isAnalyzing, loadAnalysisResults]);

  // Stop analyzing if all documents are completed or failed, and fetch results one last time
  useEffect(() => {
    if (isAnalyzing && analysisProgress) {
      const { completed, failed, total } = analysisProgress;
      if ((completed + failed === total) && total > 0) {
        setIsAnalyzing(false);
        loadAnalysisResults(); // Fetch results when analysis completes
      }
    }
  }, [isAnalyzing, analysisProgress, loadAnalysisResults]);

  // Polling for process_synthesis and SOP after analysis completes
  useEffect(() => {
    // Exit if analysis is running or if we already have everything.
    if (isAnalyzing || (analysisResults?.process_synthesis && analysisResults?.sop)) {
      return;
    }

    let attempts = 0;
    const maxAttempts = 40; // 2 minutes timeout

    const intervalId = setInterval(() => {
      if (attempts >= maxAttempts) {
        console.warn("Post-analysis polling timed out.");
        clearInterval(intervalId);
        return;
      }

      // Polling for synthesis/SOP attempt
      loadAnalysisResults();
      attempts++;
    }, 3000);

    return () => clearInterval(intervalId);
  }, [isAnalyzing, analysisResults, loadAnalysisResults]);

  const exportSOP = async () => {
    if (!currentProject) return;

    setIsExporting(true);
    try {
      const response = await api.post(`/api/export/sop/${currentProject.id}`);
      toast.success('SOP exported successfully');
      const downloadUrl = response.data.download_url;
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:5050';
      const fullUrl = `${backendUrl}${downloadUrl}`;

      // Get the token from localStorage
      const token = localStorage.getItem('jarvis_access_token');

      // Fetch the file as a blob with Authorization header
      const fileResponse = await fetch(fullUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!fileResponse.ok) {
        throw new Error('Failed to download SOP');
      }

      const blob = await fileResponse.blob();
      const url = window.URL.createObjectURL(blob);

      // Trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = response.data.filename || 'SOP.docx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to export SOP');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Add refresh indicator */}
      {isRefreshing && (
        <div className="fixed top-4 right-4 bg-blue-500 text-white px-3 py-2 rounded-md shadow-lg z-50">
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            <span className="text-sm">Updating...</span>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-super-dark-purple">SOP Generation</h1>
          <p className="text-gray-600">Generate and manage Standard Operating Procedures</p>
        </div>
        <div className="flex space-x-3">
          <Tooltip
            text="Export Standard Operating Procedures as a formatted document ready for compliance and audit purposes."
            placement="bottom"
          >
            <button
              onClick={exportSOP}
              disabled={isExporting || !analysisResults?.sop}
              className="bg-electric-blue text-white px-4 py-2 rounded-lg hover:bg-darker-blue transition-colors disabled:opacity-50 flex items-center space-x-2"
            >
              {isExporting ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
              ) : (
                <FileText className="w-4 h-4" />
              )}
              <span>{isExporting ? 'Exporting...' : 'Export SOP'}</span>
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Project Context Display */}
      <ProjectContextDisplay />

      <div className="bg-white rounded-lg shadow-sm">
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex items-center space-x-2">
            <FileText className="w-5 h-5 text-electric-blue" />
            <h2 className="text-lg font-semibold text-gray-900">SOP Generation</h2>
          </div>
        </div>

        <div className="p-6">
          <SOPGenerationTab
            analysisResults={analysisResults}
            loadAnalysisResults={loadAnalysisResults}
            isAnalyzing={isAnalyzing}
            analysisProgress={analysisProgress}
            globalComplianceScore={complianceScore}
            onRefresh={refreshAnalysisDataWithFeedback}
          />
        </div>
      </div>

      {/* Loading Modal for Export SOP */}
      <LoadingModal
        isOpen={isExporting}
        title="Exporting SOP"
        message="Please wait while we generate and prepare your SOP document for download. This may take up to 1 minute."
      />
    </div>
  );
};

export default SopGeneration;